<script setup lang="ts">
import VChart from 'vue-echarts'

import { use } from 'echarts/core'
import { RadarChart } from 'echarts/charts'
import { RadarComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'

export type ChartRadarIndicator = {
  name: string
  max: number
  min?: number
  /** Optionales Suffix (z.B. `"m/s"`, `"SCU"`), wird im Tooltip hinter dem Wert angezeigt. */
  suffix?: string
}

export type ChartRadarSeries = {
  name: string
  values: number[]
  /** Optionale Farbe: Nuxt-UI-Semantikname (`"primary"`, `"error"`, ...) oder CSS-Farbe — ohne Angabe Standard-Palette der Reihe nach. */
  color?: string
}

// Beliebig viele Achsen (3, 5, 6, ...): `indicators`/`series` werden vom
// Aufrufer bereits auf die für das jeweilige Objekt verfügbaren Kennzahlen
// gefiltert, die Komponente selbst kennt keine feste Achsenzahl.
const props = defineProps({
  indicators: {
    type: Array as () => ChartRadarIndicator[],
    default: () => [],
  },
  series: {
    type: Array as () => ChartRadarSeries[],
    default: () => [],
  },
  height: {
    type: Number,
    default: 280,
  },
})

use([
  RadarChart,
  RadarComponent,
  TooltipComponent,
  LegendComponent,
  SVGRenderer,
])

// ECharts rendert auf <canvas>/SVG-Attribute und löst CSS-Variablen dort
// nicht selbst auf (anders als normales CSS) — `var(--ui-primary)` würde
// stillschweigend als ungültige Farbe ignoriert. Deshalb hier wie in
// `charts/ring-gauge.client.vue` einmalig über `getComputedStyle` den
// tatsächlichen Farbwert auslesen, statt die Variable durchzureichen.
function resolveCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

const UI_SEMANTIC_COLORS = new Set(['primary', 'secondary', 'success', 'info', 'warning', 'error', 'neutral'])
const DEFAULT_SERIES_COLORS = [
  resolveCssVar('--color-aris-400'),
  resolveCssVar('--color-industrial-400'),
  '#60a5fa',
  '#c084fc',
]

function resolveColor(color: string | undefined, fallback: string): string {
  if (color && UI_SEMANTIC_COLORS.has(color)) return resolveCssVar(`--ui-${color}`)
  return color ?? fallback
}

const hasLegend = computed(() => props.series.length > 1)

// Einmal aufgelöst, sowohl für's Rendern der Serien als auch für den
// Tooltip-Formatter — sonst müssten Serienfarben doppelt berechnet werden.
const resolvedSeries = computed(() =>
  props.series.map((entry, index) => ({
    ...entry,
    color: resolveColor(entry.color, DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length]),
  })),
)

const numberFormat = new Intl.NumberFormat('de-DE')

function formatValue(value: number, indicator: ChartRadarIndicator): string {
  return `${numberFormat.format(value)}${indicator.suffix ? ` ${indicator.suffix}` : ''}`
}

// Rich-Text-Styles für die Achsen-Beschriftung, ein Eintrag pro Serie (`s0`,
// `s1`, ...) — so kann der Wert je Serie in deren eigener Farbe unter dem
// Achsennamen stehen, statt nur einfarbigen Text zurückzugeben.
const axisNameRichStyles = computed(() => {
  const styles: Record<string, { color: string, fontWeight: 'bold', fontSize: number, lineHeight: number }> = {}

  resolvedSeries.value.forEach((entry, index) => {
    styles[`s${index}`] = { color: entry.color, fontWeight: 'bold', fontSize: 11, lineHeight: 13 }
  })

  return styles
})

// Zeigt unter jedem Achsennamen zusätzlich den tatsächlichen Wert an (bei
// mehreren Serien je Serie farblich passend), statt die Werte nur im
// Tooltip beim Hovern preiszugeben.
function axisNameFormatter(name = ''): string {
  const index = props.indicators.findIndex((indicator) => indicator.name === name)
  if (index === -1) return name

  const indicator = props.indicators[index]!

  if (resolvedSeries.value.length <= 1) {
    const value = resolvedSeries.value[0]?.values[index]
    return value == null ? name : `${name}\n${formatValue(value, indicator)}`
  }

  const valuesLines = resolvedSeries.value
    .map((entry, seriesIndex) => `{s${seriesIndex}|${formatValue(entry.values[index], indicator)}}`)
    .join('\n')

  return `${name}\n${valuesLines}`
}

// Liegen mehrere Serien nah beieinander oder übereinander (z.B. "Normal" vs.
// "Geboostet" bei Schiffen ohne Boost), reicht ein Tooltip pro Datenpunkt
// nicht — man sieht dann nur den einen Wert unter dem Mauszeiger. Der
// Formatter ignoriert deshalb bewusst den auslösenden Punkt und listet
// stattdessen immer alle Serien mit allen Achsenwerten auf, farblich passend
// zur jeweiligen Serie.
function tooltipFormatter(): string {
  return resolvedSeries.value
    .map((entry) => {
      const rows = props.indicators
        .map((indicator, index) => `
          <div style="display:flex;justify-content:space-between;gap:16px;">
            <span style="color:#a3a3a3;">${indicator.name}</span>
            <span style="font-weight:600;">${numberFormat.format(entry.values[index])}${indicator.suffix ? ` ${indicator.suffix}` : ''}</span>
          </div>
        `)
        .join('')

      return `
        <div style="margin-bottom:8px;">
          <div style="display:flex;align-items:center;gap:6px;font-weight:600;color:${entry.color};margin-bottom:2px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:9999px;background:${entry.color};"></span>
            ${entry.name}
          </div>
          ${rows}
        </div>
      `
    })
    .join('')
}

const option = computed(() => ({
  tooltip: {
    trigger: 'item',
    confine: true,
    backgroundColor: 'rgba(24, 24, 27, 0.96)',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: '#e5e5e5', fontSize: 12 },
    formatter: tooltipFormatter,
  },

  legend: hasLegend.value
    ? {
        bottom: 0,
        padding: 0,
        itemGap: 16,
        textStyle: { color: '#d4d4d4', fontSize: 11 },
        data: props.series.map((entry) => entry.name),
      }
    : undefined,

  radar: {
    // Radius/Center bewusst konservativ: die Achsen-Labels (z.B.
    // "SCM-Geschw.") ragen über den Radius hinaus, ein zu großer Radius lässt
    // sie aus der Card-Begrenzung herauslaufen. Bei mehreren Serien ist das
    // Label 3-zeilig (Name + ein Wert pro Serie) statt 1-zeilig — dafür
    // zusätzlich Platz am oberen Rand freihalten (kleinerer Radius, tiefer
    // liegender Mittelpunkt), sonst läuft die oberste Achsen-Beschriftung aus
    // der Card heraus und überlappt den Titel darüber.
    center: ['50%', hasLegend.value ? '60%' : '50%'],
    radius: hasLegend.value ? '62%' : '58%',

    indicator: props.indicators.map((indicator) => ({
      name: indicator.name,
      max: indicator.max,
      min: indicator.min ?? 0,
    })),

    axisName: {
      color: '#d4d4d4',
      fontSize: 11,
      lineHeight: 16,
      formatter: axisNameFormatter,
      rich: axisNameRichStyles.value,
    },

    splitArea: {
      areaStyle: {
        color: ['transparent'],
      },
    },

    splitLine: {
      lineStyle: {
        color: 'rgba(255, 255, 255, 0.12)',
      },
    },

    axisLine: {
      lineStyle: {
        color: 'rgba(255, 255, 255, 0.15)',
      },
    },
  },

  series: [
    {
      type: 'radar',

      emphasis: {
        lineStyle: { width: 3 },
      },

      data: resolvedSeries.value.map((entry) => ({
        name: entry.name,
        value: entry.values,
        symbolSize: 4,
        lineStyle: { color: entry.color, width: 2 },
        itemStyle: { color: entry.color },
        areaStyle: { color: entry.color, opacity: 0.15 },
      })),
    },
  ],
}))
</script>

<template>
  <div :style="{ height: `${height}px` }">
    <VChart
      :option="option"
      :init-options="{ renderer: 'svg' }"
      autoresize
    />
  </div>
</template>
